import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { routes } from '@/app/router/route-paths';
import { TextField } from '@/shared/forms/TextField';
import { Button } from '@/shared/ui/Button';

import { useLogin } from '../hooks/useLogin';
import { loginSchema } from '../validation/login.schema';

export function LoginForm() {
  const login = useLogin();
  const location = useLocation();
  const navigate = useNavigate();
  const form = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', remember: false },
    mode: 'onBlur',
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      const destination = location.state?.from?.pathname || routes.home;
      navigate(destination, { replace: true });
    } catch (error) {
      if (error.code === 'INVALID_CREDENTIALS' || error.status === 401) {
        form.setError('root', { message: 'Email or password is incorrect.' });
      } else {
        form.setError('root', {
          message: error.requestId
            ? `Unable to sign in. Reference: ${error.requestId}`
            : 'Unable to sign in right now. Please try again.',
        });
      }
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-5">
      <TextField
        label="Email"
        required
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        error={form.formState.errors.email?.message}
        {...form.register('email')}
      />
      <TextField
        label="Password"
        required
        type="password"
        autoComplete="current-password"
        error={form.formState.errors.password?.message}
        {...form.register('password')}
      />
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 rounded border" {...form.register('remember')} />
          Keep me signed in
        </label>
        <Link
          to={routes.forgotPassword}
          className="text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          Forgot password?
        </Link>
      </div>
      {form.formState.errors.root && (
        <p
          role="alert"
          className="rounded-lg bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300"
        >
          {form.formState.errors.root.message}
        </p>
      )}
      <Button type="submit" loading={login.isPending} className="w-full">
        Sign in
      </Button>

      <p className="text-center text-sm text-[var(--app-muted)]">
        New to Ayat?{' '}
        <Link
          to={routes.register}
          className="font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          Create an account
        </Link>
      </p>
    </form>
  );
}
